/**
 * The PostHog client wrapper. Everything the app calls goes through here so
 * that "analytics is off" is a real, total no-op rather than a config flag the
 * SDK still reads.
 *
 * Three properties this module guarantees:
 *
 *   1. **Silent when unconfigured.** No key, or the kill switch off, and
 *      `start()` never imports the SDK — no network, no cookies, no globals.
 *      That's what keeps `vitest`, Playwright, and offline local dev clean.
 *   2. **Lazily loaded.** posthog-js is a dynamic import, so it lands in its
 *      own chunk instead of the main bundle and is never fetched when off.
 *   3. **Scrubbed at the door.** `sanitize_properties` runs on every event,
 *      including the SDK's own built-ins that attach `window.location` behind
 *      our back. See sanitize.ts.
 *
 * Config comes from the backend (`GET /api/posthog/config`), not from a
 * `VITE_*` var: those bake in at image build time, so rotating a key would
 * mean a rebuild. Same reasoning as the Anthropic/OpenAI keys in Settings.
 */

import type { PostHog } from "posthog-js";

import { sanitizeProperties } from "./sanitize";

export interface AnalyticsConfig {
  enabled: boolean;
  project_key: string | null;
  api_host: string;
  ui_host: string;
  /** "local" | "dev" | "prod" — stamped onto every event. */
  environment?: string;
}

export interface AnalyticsUser {
  id: string;
  email: string;
  name: string;
  roles: string[];
}

let client: PostHog | null = null;
let starting: Promise<PostHog | null> | null = null;

/**
 * Events fired before the SDK finishes loading. The first page view is the
 * most interesting one in the session (it's the entry point), so dropping it
 * would bias every landing-page question. Bounded so a misconfigured instance
 * can't grow this without limit.
 */
const MAX_PENDING = 25;
const pending: { event: string; properties?: Record<string, unknown> }[] = [];

export function isStarted(): boolean {
  return client !== null;
}

/**
 * Boot the SDK. Safe to call repeatedly — later calls return the same client.
 * Resolves to null when analytics is off, which is the normal local-dev state.
 */
export async function start(config: AnalyticsConfig): Promise<PostHog | null> {
  if (!config.enabled || !config.project_key) return null;
  if (client) return client;
  starting ??= (async () => {
    try {
      const { default: posthog } = await import("posthog-js");
      posthog.init(config.project_key as string, {
        api_host: config.api_host,
        ui_host: config.ui_host,
        // We emit our own page views from the router so they carry the feature
        // key and land after the route resolves, not before.
        capture_pageview: false,
        capture_pageleave: true,
        // No autocapture: it would hoover up every click target's text, which
        // on this app means customer names and dollar amounts.
        autocapture: false,
        disable_session_recording: true,
        // Belt and braces — if recording is ever switched on from Settings, it
        // starts masked rather than filming the P&L.
        session_recording: { maskAllInputs: true, maskTextSelector: "*" },
        // Only build person profiles for users we've explicitly identified.
        person_profiles: "identified_only",

        // The three below are ON by default in PostHog's *server-side* remote
        // config, and none of them are things we asked for. Pin them here so
        // the client is the authority: someone flipping a toggle in the PostHog
        // UI must not be able to start collecting from an app that renders
        // Cinch's P&L. Verified against this project's /array/<key>/config,
        // which reports heatmaps:true and capturePerformance:{network_timing,
        // web_vitals}.
        //
        // network_timing is the pointed one: it captures resource request URLs,
        // which here means /api/contracts/<uuid> and friends. Those ride inside
        // a performance payload, NOT in the $current_url property, so
        // sanitize_properties would never see them.
        capture_performance: false,
        enable_heatmaps: false,
        // Off at the project too, but without this the SDK still fetches the
        // surveys module on every load just to no-op. We're not running
        // surveys from an internal ops tool.
        disable_surveys: true,

        // Exceptions ARE captured — deliberately, and it's the one default we
        // turn *on*. "Where do people hit errors" locates friction far more
        // precisely than open-counts do: a feature with steady traffic and a
        // steady error rate is a different problem from one nobody opens.
        // Free tier covers 100K exceptions/month; we won't approach that.
        //
        // Safe alongside the rest of this config: an $exception event carries
        // the error type, message, and stack — application code, not screen
        // contents — and its URL properties go through sanitize_properties
        // like everything else. It does NOT enable autocapture or recording.
        capture_exceptions: true,

        sanitize_properties: sanitizeProperties,
      });

      // Stamp the environment onto every event as a super-property.
      //
      // Local and dev point at the same PostHog project as production, so
      // without this a developer clicking around is indistinguishable from
      // real usage — and the weekly review would set goals against numbers
      // padded with our own testing. Capture stays ON outside prod so the
      // instrumentation itself can be verified; the dashboard filters to prod
      // (see metrics.PROD_ONLY).
      posthog.register({ environment: config.environment ?? "prod" });

      client = posthog;
      for (const item of pending.splice(0, pending.length)) {
        posthog.capture(item.event, item.properties);
      }
      return posthog;
    } catch (err) {
      // A blocked or failed SDK load must never take the app down with it.
      console.warn("[analytics] disabled — SDK failed to load", err);
      starting = null;
      return null;
    }
  })();
  return starting;
}

/** Emit an event. No-op when analytics is off; queued while the SDK loads. */
export function track(event: string, properties?: Record<string, unknown>): void {
  if (client) {
    client.capture(event, properties);
    return;
  }
  if (starting && pending.length < MAX_PENDING) pending.push({ event, properties });
}

/**
 * Tie the session to a person. Internal staff, so email and name are the
 * point — this is how per-user goals are scored. Roles ride along so the
 * weekly review can reason about "did the people who *should* use this, use
 * it".
 */
export function identify(user: AnalyticsUser): void {
  client?.identify(user.id, {
    email: user.email,
    name: user.name,
    roles: user.roles,
  });
}

/** Drop identity on sign-out so the next session doesn't inherit it. */
export function reset(): void {
  client?.reset();
}

/** Test seam. */
export function __resetForTests(): void {
  client = null;
  starting = null;
  pending.length = 0;
}
